// src/context/AuthContext.js
import { createContext, useReducer, useEffect, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { onUserLogin, onUserLogout, isNativeApp } from '../utils/nativeApp'
import api from '../api/api'
import { SESSION_KEYS, adoptExternalChange, currentGeneration, endSession, getAccessToken, getRefreshToken, getStoredUser, isCurrentGeneration, startSession, storeUser, subscribe } from '../auth/session'

const initialState = () => ({
  user: getStoredUser(),
  token: getAccessToken()
})

// The reducer only mirrors the session into React state. Storage belongs to
// the session module and is written by `dispatch` below, outside the reducer
// (React may run a reducer twice), so every path — sign-in, refresh, expiry,
// another tab — goes through one place and a stale request cannot write over
// a newer session.
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN':
      return { user: action.payload.user, token: action.payload.token }
    case 'LOGOUT':
      return { user: null, token: null }
    case 'SYNC':
      return { user: getStoredUser(), token: getAccessToken() }
    case 'UPDATE_USER':
      return { ...state, user: { ...(state.user || {}), ...(action.payload || {}) } }
    default:
      return state
  }
}

export const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [state, rawDispatch] = useReducer(authReducer, undefined, initialState)
  const queryClient = useQueryClient()
  const userIdRef = useRef(state.user?.id ?? null)

  const dispatch = useCallback(action => {
    switch (action?.type) {
      case 'LOGIN': {
        const { token, refresh, user } = action.payload
        startSession({ access: token, refresh, user })
        onUserLogin(token)
        break
      }
      case 'LOGOUT':
        if (getAccessToken()) {
          endSession('logout')
          onUserLogout()
        }
        break
      case 'UPDATE_USER':
        storeUser({ ...(getStoredUser() || {}), ...(action.payload || {}) })
        break
      default:
        break
    }
    rawDispatch(action)
  }, [])

  // Anything cached for one customer must not be shown to the next one.
  const forgetCustomerData = useCallback(() => {
    queryClient.clear()
  }, [queryClient])

  // Session changes that did not come from a dispatch here: a refresh token
  // the server refused, a token rotation, or another tab signing in or out.
  useEffect(() => {
    const unsubscribe = subscribe(type => {
      if (type === 'end' || type === 'refreshed' || type === 'external') rawDispatch({ type: 'SYNC' })
    })
    const onStorage = event => {
      if (event.key !== null && !Object.values(SESSION_KEYS).includes(event.key)) return
      adoptExternalChange()
    }
    window.addEventListener('storage', onStorage)
    return () => {
      unsubscribe()
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  useEffect(() => {
    const id = state.user?.id ?? null
    if (userIdRef.current !== null && userIdRef.current !== id) forgetCustomerData()
    userIdRef.current = id
  }, [state.user?.id, forgetCustomerData])

  // Session restoration: confirm the stored session with the server once per
  // page load (renewing an expired access token on the way) and pick up
  // profile changes made elsewhere. Never blocks rendering.
  useEffect(() => {
    if (!getAccessToken()) return undefined
    const startedIn = currentGeneration()
    let cancelled = false
    api
      .get('/auth/profile', { suppressErrorToast: true })
      .then(({ data }) => {
        if (cancelled || !isCurrentGeneration(startedIn) || !data || typeof data !== 'object') return
        dispatch({ type: 'UPDATE_USER', payload: data })
      })
      .catch(() => {
        /* an expired session is ended by the API client; anything else keeps the stored one */
      })
    return () => {
      cancelled = true
    }
  }, [dispatch])

  // AUTO-REGISTER DEVICE ON APP STARTUP for users signed in before the native bridge existed.
  useEffect(() => {
    const token = getAccessToken()
    if (token && isNativeApp()) {
      const timer = setTimeout(() => onUserLogin(token), 500)
      return () => clearTimeout(timer)
    }
  }, [])

  /**
   * Sign out: end the session here at once, then tell the server so the
   * refresh token is blacklisted and the access token denied. The server
   * call is best-effort — being offline must not keep anyone signed in.
   */
  const logout = useCallback(
    async ({ allDevices = false } = {}) => {
      const access = getAccessToken()
      const refresh = getRefreshToken()
      dispatch({ type: 'LOGOUT' })
      forgetCustomerData()
      // A session from before refresh tokens has nothing the server can blacklist.
      if (!access || (!allDevices && !refresh)) return
      try {
        await api.post('/auth/logout', allDevices ? { all_devices: true } : { refresh }, { headers: { Authorization: `Bearer ${access}` }, suppressErrorToast: true })
      } catch {
        /* already signed out locally */
      }
    },
    [dispatch, forgetCustomerData]
  )

  return <AuthContext.Provider value={{ ...state, dispatch, logout }}>{children}</AuthContext.Provider>
}
